import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Alert, Divider } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { signInWithEmailAndPassword } from 'firebase/auth'
import * as React from 'react'

import { useAnalytics } from '../../contexts/AnalyticsContext'
import db from '../../db/db'
import initFirebase from '../../db/initFirebase'
import { AnalyticsService } from '../../services/AnalyticsService'
import { AnalyticsEvents, AuthEventParams } from '../../types/AnalyticsEvents'
import Database from '../../types/Database'
import { signInWithOAuth, OAuthProviderType } from '../../utils/oauthHelpers'

import PreLoginCard from './PreLoginCard'

interface SignInProps {
  onAuthStart?: () => void
}

/**
 * Helper function to set user analytics properties after successful login
 * Validates: Requirements 2.3, 13.5, 13.6
 */
const setUserAnalyticsProperties = async (userId: string) => {
  try {
    // Set user ID
    AnalyticsService.setUserId(userId)

    // Get user data to determine role
    const userRecord = (await db.getById(
      'users',
      userId
    )) as Database.User | null

    if (userRecord) {
      const role = userRecord.isAdmin ? 'admin' : 'user'

      // Get user's first office to determine subscription tier
      let subscriptionTier: Database.SubscriptionTier = 'free'
      if (userRecord.officeids && userRecord.officeids.length > 0) {
        const firstOfficeId = userRecord.officeids[0]
        const office = (await db.getById(
          'offices',
          firstOfficeId
        )) as Database.Office | null
        if (office?.subscriptionTier) {
          subscriptionTier = office.subscriptionTier
        }
      }

      // Set user properties
      AnalyticsService.setUserProperties({
        role,
        subscription_tier: subscriptionTier
      })
    }
  } catch (error) {
    console.error('Failed to set user analytics properties:', error)
    // Don't throw - analytics failures should not break login
  }
}

export default function SignIn({ onAuthStart }: SignInProps) {
  const [authError, setAuthError] = React.useState<string>('')
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const { trackEvent } = useAnalytics()

  const handleOAuthSignIn = async (provider: OAuthProviderType) => {
    setIsLoading(true)
    setAuthError('')
    if (onAuthStart) {
      onAuthStart()
    }

    const result = await signInWithOAuth(provider)

    if (result.success && result.userCredential) {
      // Check if user record exists in database
      try {
        const userRecord = await db.getById(
          'users',
          result.userCredential.user.uid
        )

        if (!userRecord) {
          // User authenticated but no account exists - sign them out
          console.log('OAuth user has no account record, signing out')
          await initFirebase.getFireBaseStore().auth.signOut()
          setIsLoading(false)
          setAuthError(
            'No account found. Please sign up first to create an account.'
          )
          return
        }

        // User exists, track login event
        console.log(`${provider} sign-in successful`)

        // Track login event with authentication method
        const authMethod = provider === 'google' ? 'google' : 'other'
        const eventParams: AuthEventParams = { method: authMethod }
        trackEvent(AnalyticsEvents.LOGIN, eventParams)

        // Set user ID and properties for analytics
        await setUserAnalyticsProperties(result.userCredential.user.uid)

        // Auth state change will handle redirect
      } catch (err) {
        console.error('Error checking user record:', err)
        await initFirebase.getFireBaseStore().auth.signOut()
        setIsLoading(false)
        setAuthError('Failed to verify account. Please try again.')
      }
    } else {
      setIsLoading(false)
      setAuthError(result.error || 'Authentication failed')
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    if (onAuthStart) {
      onAuthStart()
    }
    const data = new FormData(event.currentTarget)
    const auth = initFirebase.getFireBaseStore().auth
    var email = data.get('email')?.toString() ?? ''
    var password = data.get('password')?.toString() ?? ''
    signInWithEmailAndPassword(auth, email, password).then(
      async userCredential => {
        // Track login event with authentication method
        const eventParams: AuthEventParams = { method: 'email' }
        trackEvent(AnalyticsEvents.LOGIN, eventParams)

        // Set user ID and properties for analytics
        await setUserAnalyticsProperties(userCredential.user.uid)

        // Keep loading state true, redirect will happen via auth state change
      },
      reason => {
        setIsLoading(false)
        var basicError = 'Login failed. '
        if (reason.toString().includes('user-not-found'))
          basicError = basicError + 'Invalid credentials'
        if (reason.toString().includes('missing-email'))
          basicError = basicError + 'No username was provided'
        setAuthError(basicError)
      }
    )
  }

  return (
    <PreLoginCard>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Avatar sx={{ bgcolor: '#2563eb', width: 40, height: 40 }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography
            component='h1'
            variant='h5'
            sx={{ fontWeight: 700, color: '#1e293b' }}
          >
            Sign in
          </Typography>
        </Box>

        {/* OAuth Buttons */}
        <Box sx={{ width: '100%', mb: 2 }}>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => handleOAuthSignIn('google')}
            disabled={isLoading}
            startIcon={
              <Box
                component='img'
                src='https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg'
                alt='Google'
                sx={{ width: 20, height: 20 }}
              />
            }
            sx={{
              mb: 1.5,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#dadce0',
              color: '#3c4043',
              '&:hover': {
                borderColor: '#d2d3d4',
                backgroundColor: '#f8f9fa'
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Continue with Google
          </Button>

          <Button
            fullWidth
            variant='outlined'
            onClick={() => handleOAuthSignIn('microsoft')}
            disabled={isLoading}
            startIcon={
              <Box
                component='img'
                src='https://learn.microsoft.com/en-us/entra/identity-platform/media/howto-add-branding-in-apps/ms-symbollockup_mssymbol_19.svg'
                alt='Microsoft'
                sx={{ width: 20, height: 20 }}
              />
            }
            sx={{
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#8c8c8c',
              color: '#5e5e5e',
              '&:hover': {
                borderColor: '#737373',
                backgroundColor: '#f3f2f1'
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Continue with Microsoft
          </Button>
        </Box>

        <Divider sx={{ width: '100%', mb: 2 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary', px: 2 }}>
            or
          </Typography>
        </Divider>

        <Box
          component='form'
          onSubmit={handleSubmit}
          noValidate
          sx={{ mt: 1, width: '100%' }}
        >
          <TextField
            margin='normal'
            required
            fullWidth
            id='email'
            label='Email Address'
            name='email'
            autoComplete='email'
            autoFocus
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff !important'
              },
              '& .MuiInputLabel-root': {
                color: '#64748b'
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#2563eb'
              },
              '& .MuiOutlinedInput-input': {
                color: '#1e293b'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)'
              },
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 100px #ffffff inset !important',
                WebkitTextFillColor: '#1e293b !important',
                caretColor: '#1e293b'
              },
              '& input:-webkit-autofill:hover': {
                WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
              },
              '& input:-webkit-autofill:focus': {
                WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
              }
            }}
          />
          <TextField
            margin='normal'
            required
            fullWidth
            name='password'
            label='Password'
            type='password'
            id='password'
            autoComplete='current-password'
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff !important'
              },
              '& .MuiInputLabel-root': {
                color: '#64748b'
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#2563eb'
              },
              '& .MuiOutlinedInput-input': {
                color: '#1e293b'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)'
              },
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 100px #ffffff inset !important',
                WebkitTextFillColor: '#1e293b !important',
                caretColor: '#1e293b'
              },
              '& input:-webkit-autofill:hover': {
                WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
              },
              '& input:-webkit-autofill:focus': {
                WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
              }
            }}
          />
          <Button
            type='submit'
            fullWidth
            variant='contained'
            sx={{
              mt: 3,
              mb: 2,
              backgroundColor: '#2563eb',
              color: '#ffffff',
              fontWeight: 600,
              padding: '12px',
              fontSize: '1rem',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#1e40af'
              },
              '&:disabled': {
                backgroundColor: '#9ca3af'
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
          {authError !== '' && (
            <Alert
              severity='error'
              onClose={() => setAuthError('')}
              sx={{ mb: 2 }}
            >
              {authError}
            </Alert>
          )}
          <Grid container justifyContent='space-between'>
            <Grid item>
              <Link
                href='/forgot-password'
                variant='body2'
                sx={{
                  color: '#2563eb',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Forgot password?
              </Link>
            </Grid>
            <Grid item>
              <Link
                href='/signup'
                variant='body2'
                sx={{
                  color: '#2563eb',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {"Don't have an account? Sign Up"}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </PreLoginCard>
  )
}
